import {
  DateRangePicker,
  type DateValue,
  type DateRangePickerProps as Props,
  type RangeValue,
} from '@heroui/react';
import { getLocalTimeZone, parseDate, today } from '@internationalized/date';
import { type Control, Controller, type FieldValues, type Path, type PathValue } from 'react-hook-form';

export const RangeDatePicker = <T extends DateValue>(props: Props<T>) => {
  return <DateRangePicker radius='sm' visibleMonths={2} maxValue={today(getLocalTimeZone())} {...props} />;
};

export interface FormRangeDatePickerProps<T extends FieldValues> extends Props {
  name: Path<T>;
  control: Control<T, unknown>;
  defaultValue?: PathValue<T, Path<T>>;
  placeholder?: string;
}

export const FormRangeDatePicker = <T extends FieldValues>({
  control,
  name,
  children,
  defaultValue,
  ...rest
}: FormRangeDatePickerProps<T>) => {
  return (
    <Controller
      name={name}
      defaultValue={defaultValue || ({ start: undefined, end: undefined } as never)}
      control={control}
      shouldUnregister
      render={({ field: { onChange, onBlur, value }, fieldState: { invalid, isDirty } }) => {
        const onHandleChange = (value: RangeValue<DateValue> | null) => {
          if (!value) {
            onChange(value);
            return;
          }
          onChange({
            start: value.start.toString(),
            end: value.end.toString(),
          });
        };

        return (
          <RangeDatePicker
            {...rest}
            value={
              {
                start: value.start ? parseDate(value.start) : undefined,
                end: value.end ? parseDate(value.end) : undefined,
              } as never
            }
            isInvalid={isDirty && invalid}
            onBlur={onBlur}
            onChange={onHandleChange}
          >
            {children}
          </RangeDatePicker>
        );
      }}
    />
  );
};
